function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from "react";
import PropTypes from "prop-types";
//Material imports
import makeStyles from "@mui/styles/makeStyles";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import MuiInputBase from "@mui/material/InputBase";
import Typography from "@mui/material/Typography";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
//Limitless imports
import { useAppConfig } from "@limitless/common/hooks";
import AngleDown from "@limitless/icons/AngleDown";
import { useTranslation } from "@limitless/common/hooks";
import { parseMfaDialCodeAndPhone, getMfaDialCodeDropdownOptions, parseDialCodeInputValue } from "@limitless/portal/utils";
const DialPrefixWithPhoneInput = ({
  handlePhoneInputChange,
  value,
  disabled = false,
  label,
  dropdownListStyle,
  dropdownPaperStyle,
  dropdownContainerStyle,
  error = false,
  errorText,
  disableCountrySelection = false,
  ...rest
}) => {
  const {
    config: {
      dialCodes
    }
  } = useAppConfig();
  const [dialCodeValue, setDialCodeValue] = React.useState("");
  const [phoneNumberValue, setPhoneNumberValue] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef();
  const {
    t
  } = useTranslation();
  const classes = useStyles();
  React.useEffect(() => {
    const parseValue = parseMfaDialCodeAndPhone(value, dialCodes);
    setDialCodeValue(parseValue.dialCodeValue);
    setPhoneNumberValue(parseValue.phoneNumberValue);
  }, [value]);
  const handleTextInputBlurEvent = () => {
    if (open) {
      setOpen(false);
    }
  };
  const options = getMfaDialCodeDropdownOptions(dialCodes);
  return /*#__PURE__*/React.createElement(FormControl, {
    disabled: disabled,
    error: error,
    sx: {
      width: "300px"
    }
  }, /*#__PURE__*/React.createElement(InputLabel, null, t(label)), /*#__PURE__*/React.createElement(MuiInputBase, _extends({
    disabled: disabled,
    value: phoneNumberValue,
    onChange: e => {
      handlePhoneInputChange(parseDialCodeInputValue(dialCodeValue), e.target.value.trim());
    },
    onBlur: handleTextInputBlurEvent,
    inputRef: inputRef,
    error: error,
    classes: {
      focused: classes.focusedStyle,
      error: classes.errorStyle,
      ...classes
    }
  }, rest, {
    startAdornment: /*#__PURE__*/React.createElement(Autocomplete, {
      disabled: disabled || disableCountrySelection,
      popupIcon: disabled || disableCountrySelection ? null : /*#__PURE__*/React.createElement(AngleDown, null),
      renderInput: params => {
        var _parseDialCodeInputVa;
        const textInputProps = {
          ...params
        };
        textInputProps.inputProps.value = (_parseDialCodeInputVa = parseDialCodeInputValue(params.inputProps.value)) !== null && _parseDialCodeInputVa !== void 0 ? _parseDialCodeInputVa : "";
        return /*#__PURE__*/React.createElement(TextField, _extends({}, textInputProps, {
          classes: {
            root: classes.dropdownInput
          }
        }));
      },
      onFocus: () => {
        var _inputRef$current;
        return inputRef === null || inputRef === void 0 ? void 0 : (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
      },
      filterOptions: options => options,
      isOptionEqualToValue: (option, value) => option === value || value === "",
      disableClearable: true,
      open: open && !disableCountrySelection,
      onOpen: () => !disableCountrySelection && setOpen(true),
      onClose: () => setOpen(false),
      options: options,
      componentsProps: {
        popper: {
          placement: "bottom-start"
        }
      },
      value: dialCodeValue,
      onChange: (e, value) => {
        if (!disableCountrySelection) {
          setDialCodeValue(value);
          handlePhoneInputChange(parseDialCodeInputValue(value), phoneNumberValue);
        }
      },
      classes: {
        root: classes.autoCompleteStyle,
        listbox: `${dropdownListStyle ? dropdownListStyle : ""}`,
        popper: `${classes.popperStyle} ${dropdownContainerStyle ? dropdownContainerStyle : ""}`,
        paper: `${dropdownPaperStyle ? dropdownPaperStyle : ""}`
      },
      renderOption: (optionProps, option) => /*#__PURE__*/React.createElement("li", optionProps, /*#__PURE__*/React.createElement(Typography, {
        variant: "caption",
        color: "textPrimary"
      }, option))
    })
  })), error && /*#__PURE__*/React.createElement(FormHelperText, {
    classes: {
      root: classes.helperTextStyle
    }
  }, t(errorText)));
};
const useStyles = makeStyles(theme => ({
  autoCompleteStyle: {
    "& .MuiOutlinedInput-notchedOutline": {
      borderWidth: 0
    },
    "& .MuiOutlinedInput-root.Mui-disabled": {
      borderWidth: 0
    },
    "& .MuiOutlinedInput-root": {
      marginTop: 0,
      height: "56px",
      left: -1,
      paddingLeft: 0,
      paddingRight: "30px !important"
    }
  },
  focusedStyle: {
    borderColor: theme.palette.primary.light
  },
  popperStyle: {
    left: "-16px !important",
    minWidth: "300px"
  },
  dropdownInput: {
    padding: 0,
    marginBottom: 0,
    width: "82px"
  },
  errorStyle: {
    borderColor: theme.palette.error.dark
  },
  helperTextStyle: {
    marginLeft: 0
  }
}));
export default DialPrefixWithPhoneInput;
DialPrefixWithPhoneInput.propTypes = {
  disabled: PropTypes.bool,
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
  handlePhoneInputChange: PropTypes.func.isRequired,
  dropdownListStyle: PropTypes.string,
  dropdownPaperStyle: PropTypes.string,
  dropdownContainerStyle: PropTypes.string,
  error: PropTypes.bool,
  errorText: PropTypes.string,
  disableCountrySelection: PropTypes.bool
};